// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

// eslint-disable-next-line spire/fenced-imports
import { OffsetOptions, ShiftOptions } from "@floating-ui/dom";
import { UseHoverProps } from "@floating-ui/react/dist/floating-ui.react";

import {
    FloatingArrow,
    FloatingArrowProps,
    FloatingNode,
    FloatingPortal,
    FloatingTree,
    Placement,
    useFloatingParentNodeId,
    useTransitionStyles,
} from "@floating-ui/react";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { get } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { TooltipContext } from "@vanmeter/App/Components/FloatingTooltip/TooltipContext";
import { xHeaderUpdate } from "@vanmeter/App/Handlers/XNG/Header";
import { FocusableSelectors, useTooltip } from "@vanmeter/App/Hooks/useTooltip";
import Button, { ButtonIcon, ButtonPresentationProps } from "@vanmeter/MobiusOverrides/Button/Button";
import X from "@vanmeter/XDL/Icons/IconClose";
import {
    addScrollbarGutter,
    color,
    getSystemColor,
    mergeWithCss,
    removeScrollbarGutter,
} from "@vanmeter/XDL/Utilities";
import React, { FC, FocusEvent, useEffect, useRef, useState } from "react";
import { css, ThemeProps, withTheme } from "styled-components";
import translate from "@insite/client-framework/Translate";

export type FloatingTooltipProps = {
    isOpen?: boolean;
    placement?: Placement;
    trigger?: "hover" | "click" | "manual";
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    offset?: OffsetOptions;
    shift?: ShiftOptions;
    delay?: UseHoverProps["delay"];
    showArrow?: boolean;
    overlay?: boolean;
    disableScrollDismiss?: boolean;
    triggerComponent: React.ReactNode;
    enabled?: boolean;
    drawer?: boolean;
    extendedStyles?: FloatingTooltipStyles;
};

interface OwnProps extends FloatingTooltipProps {
    children: React.ReactNode;
}

type Props = OwnProps & ThemeProps<BaseTheme>;

export interface FloatingTooltipStyles {
    tooltipContent?: InjectableCss;
    tooltipArrow?: Omit<FloatingArrowProps, "context">;
    drawerContent?: InjectableCss;
    drawerClose?: ButtonPresentationProps;
    triggerWrapper?: InjectableCss;
}

export const floatingTooltipStyles: FloatingTooltipStyles = {
    tooltipContent: {
        css: css`
            background-color: ${color("common.ui1")};
            ${({ resolvedZIndex }: any) => css`
                z-index: ${resolvedZIndex};
            `};
            box-shadow:
                0 0 0 1px ${color("common.border")},
                0 2px 4px 0 ${color("common.black", 0.1)};
            padding: 8px;
            border-radius: 2px;
            max-width: 250px;
        `,
    },

    tooltipArrow: {
        width: 16,
        height: 8,
        fill: getSystemColor("common.ui1"),
        stroke: `${getSystemColor("common.border")}`,
        strokeWidth: 1,
    },

    drawerContent: {
        css: css`
            background-color: ${color("common.ui1")};
            display: flex;
            position: fixed;
            flex-direction: column;
            right: -400px;
            top: 0;
            height: 100vh;
            padding: 16px;
            width: 332px;
            ${({ resolvedZIndex }: any) => css`
                z-index: ${resolvedZIndex};
            `};
            transition: right 0.5s ease-in-out;

            ${({ isMounted }: any) =>
                isMounted &&
                css`
                    right: 2px;
                `}
        `,
    },

    drawerClose: {
        variant: "link" as any,
        css: css`
            margin: 0 0 8px auto;
            ${ButtonIcon} {
                color: ${color("common.ui7")};
            }
        `,
    },
};

const FloatingTooltipComponent: FC<Props> = ({
    children,
    enabled = true,
    triggerComponent,
    overlay = true,
    disableScrollDismiss = false,
    extendedStyles,
    showArrow = true,
    drawer = false,
    theme,
    ...options
}) => {
    const pathname = useAppSelector(o => getLocation(o).pathname);
    const arrowRef = useRef<SVGSVGElement>(null);
    const tooltip = useTooltip({ ...options, showArrow, arrowRef });
    const { isMounted, styles: translationStyles } = useTransitionStyles(tooltip.context);
    const [zIndexKey, setZIndexKey] = React.useState("");

    const [styles] = useState(() => mergeWithCss(floatingTooltipStyles, extendedStyles));
    const dispatch = useAppDispatch();

    const resolvedZIndex = get(theme, `zIndex.${zIndexKey || (drawer ? "drawer" : "tooltip")}`);

    React.useEffect(() => {
        if (
            typeof document !== "undefined" &&
            (document.getElementById("modalTitle") || document.getElementById("drawerTitle"))
        ) {
            setZIndexKey("tooltipInModalOrDrawer");
        }
    }, []);
    // Close tooltip when scrolling outside its bounds
    useEffect(() => {
        if (disableScrollDismiss) {
            return;
        }

        const handleScroll = (event: Event) => {
            const tooltipElement = tooltip.refs.floating?.current;
            if (tooltipElement && event.target instanceof Node && tooltipElement.contains(event.target)) {
                return;
            }

            tooltip.setOpen(false);
            options.onOpenChange?.(false);
        };

        window.addEventListener("scroll", handleScroll, true);

        return () => {
            window.removeEventListener("scroll", handleScroll, true);
        };
    }, [disableScrollDismiss, tooltip.refs.floating, tooltip.setOpen]);

    useEffect(() => {
        if (overlay) {
            dispatch(
                xHeaderUpdate({
                    key:
                        isMounted && (options.trigger !== "manual" || options.isOpen)
                            ? "registerMenu"
                            : "unregisterMenu",
                    value: drawer ? `${tooltip.nodeId}_drawer` : `${tooltip.nodeId}_tooltip`,
                }),
            );
            if (drawer) {
                isMounted ? document.body.classList.add("drawer-open") : document.body.classList.remove("drawer-open");
            }
        }
    }, [isMounted, overlay, options.isOpen]);

    useEffect(() => {
        tooltip.setOpen(false);
        options.onOpenChange?.(false);
        if (overlay) {
            dispatch(
                xHeaderUpdate({
                    key: "unregisterMenu",
                    value: drawer ? `${tooltip.nodeId}_drawer` : `${tooltip.nodeId}_tooltip`,
                }),
            );
        }
    }, [pathname]);

    useEffect(() => {
        return () => {
            tooltip.setOpen(false);
            options.onOpenChange?.(false);
            if (overlay) {
                dispatch(
                    xHeaderUpdate({
                        key: "unregisterMenu",
                        value: drawer ? `${tooltip.nodeId}_drawer` : `${tooltip.nodeId}_tooltip`,
                    }),
                );

                if (drawer) {
                    document.body.classList.remove("drawer-open");
                }
            }
        };
    }, []);

    if (!enabled) {
        return <>{triggerComponent}</>;
    }

    return (
        <FloatingNode id={tooltip.nodeId}>
            <TooltipContext.Provider value={{ ...tooltip, open: isMounted }}>
                <StyledWrapper
                    ref={tooltip.refs.setReference}
                    {...tooltip.getReferenceProps()}
                    data-state={isMounted ? "open" : "closed"}
                    {...styles.triggerWrapper}
                >
                    {triggerComponent}
                </StyledWrapper>
                <FloatingPortal>
                    <StyledWrapper
                        ref={tooltip.refs.setFloating}
                        onClick={(e: React.MouseEvent) => {
                            const link = (e.target as HTMLElement).closest("a");
                            if (link) {
                                tooltip.setOpen(false);
                                options.onOpenChange?.(false);
                            }
                        }}
                        style={
                            !drawer
                                ? {
                                      ...tooltip.floatingStyles,
                                      ...translationStyles,
                                      display: isMounted ? "block" : "none",
                                  }
                                : {}
                        }
                        {...(drawer ? styles.drawerContent : styles.tooltipContent)}
                        {...{ resolvedZIndex }}
                        {...{ isMounted }}
                        {...tooltip.getFloatingProps({
                            onFocus(event: FocusEvent<HTMLElement>) {
                                const target = event.target as HTMLElement;
                                if (
                                    (options.trigger === "hover" && target.matches(FocusableSelectors)) ||
                                    target.isContentEditable
                                ) {
                                    tooltip.setHoverEnabled(false);
                                }
                            },
                            onBlur(event: FocusEvent<HTMLElement>) {
                                const target = event.target as HTMLElement;
                                if (
                                    (options.trigger === "hover" && target.matches(FocusableSelectors)) ||
                                    target.isContentEditable
                                ) {
                                    tooltip.setHoverEnabled(true);
                                }
                            },
                        })}
                    >
                        {drawer && (
                            <Button
                                {...styles.drawerClose}
                                onClick={() => tooltip.setOpen(false)}
                                aria-label={translate("Close")}
                            >
                                <ButtonIcon src={X} size={15} />
                            </Button>
                        )}
                        {children}
                        {!drawer && showArrow && (
                            <FloatingArrow ref={arrowRef} context={tooltip.context} {...styles.tooltipArrow} />
                        )}
                    </StyledWrapper>
                </FloatingPortal>
            </TooltipContext.Provider>
        </FloatingNode>
    );
};

export function FloatingTooltip(props: Props) {
    const parentId = useFloatingParentNodeId();
    if (parentId === null) {
        return (
            <FloatingTree>
                <FloatingTooltipComponent {...props} />
            </FloatingTree>
        );
    }
    return <FloatingTooltipComponent {...props} />;
}

export default withTheme(FloatingTooltip);
